import {Banner} from '@primer/react'
import {useArchiveDialog, ARCHIVE_STATUS, type ArchiveStatus} from '../contexts/ArchiveDialogContext'
import {useUnarchiveTask} from '../hooks/use-unarchive-task'
import {useReopenPullRequest} from '../hooks/use-reopen-pull-request'

interface BannerConfig {
  variant: 'success' | 'critical' | 'warning'
  title: string
  description: string
  undoAction?: 'unarchive' | 'unarchiveAndReopen'
}

const BANNER_CONFIG: Partial<Record<ArchiveStatus, BannerConfig>> = {
  [ARCHIVE_STATUS.SUCCESS]: {
    variant: 'success',
    title: 'Session archived',
    description: 'The session has been archived successfully.',
    undoAction: 'unarchive',
  },
  [ARCHIVE_STATUS.SUCCESS_WITH_PR_CLOSED]: {
    variant: 'success',
    title: 'Session archived and PR closed',
    description: 'The session has been archived and the pull request has been closed.',
    undoAction: 'unarchiveAndReopen',
  },
  [ARCHIVE_STATUS.ERROR]: {
    variant: 'critical',
    title: 'Archive failed',
    description: 'Failed to archive the session. Please try again.',
  },
  [ARCHIVE_STATUS.UNDO_ERROR]: {
    variant: 'critical',
    title: 'Undo failed',
    description: 'Failed to undo the archive. Please try again.',
  },
  [ARCHIVE_STATUS.ERROR_PR_CLOSE_FAILED]: {
    variant: 'warning',
    title: 'PR close failed',
    description: 'The session was archived, but the pull request could not be closed. Please close it manually.',
  },
  [ARCHIVE_STATUS.ERROR_PR_REOPEN_FAILED]: {
    variant: 'warning',
    title: 'PR reopen failed',
    description: 'The session was unarchived, but the pull request could not be reopened. Please reopen it manually.',
  },
}

export function ArchiveTaskBanner() {
  const {archiveStatus, setArchiveStatus, dialogState} = useArchiveDialog()
  const repoNwo =
    dialogState.repoOwner && dialogState.repoName ? `${dialogState.repoOwner}/${dialogState.repoName}` : undefined
  const {mutateAsync: unarchiveTaskAsync, isPending: isUnarchiving} = useUnarchiveTask({
    taskId: dialogState.taskId,
    repoNwo,
  })
  const {mutateAsync: reopenPullRequestAsync, isPending: isReopening} = useReopenPullRequest({
    repoNwo: dialogState.pull?.repository_nwo,
    prNumber: dialogState.pull?.number,
  })
  const isUndoing = isUnarchiving || isReopening

  const config = BANNER_CONFIG[archiveStatus]
  if (!config) return null

  const handleDismiss = () => setArchiveStatus(ARCHIVE_STATUS.IDLE)

  const handleUndo = async () => {
    try {
      await unarchiveTaskAsync()
      setArchiveStatus(ARCHIVE_STATUS.IDLE)
    } catch {
      setArchiveStatus(ARCHIVE_STATUS.UNDO_ERROR)
    }
  }

  const handleUndoWithReopen = async () => {
    if (!dialogState.pull?.repository_nwo || !dialogState.pull?.number) {
      handleUndo()
      return
    }
    try {
      await unarchiveTaskAsync()
      try {
        await reopenPullRequestAsync()
        setArchiveStatus(ARCHIVE_STATUS.IDLE)
      } catch {
        setArchiveStatus(ARCHIVE_STATUS.ERROR_PR_REOPEN_FAILED)
      }
    } catch {
      setArchiveStatus(ARCHIVE_STATUS.UNDO_ERROR)
    }
  }

  const undoHandler = config.undoAction === 'unarchiveAndReopen' ? handleUndoWithReopen : handleUndo
  const showUndo = config.undoAction

  return (
    <Banner
      variant={config.variant}
      title={config.title}
      hideTitle
      description={config.description}
      onDismiss={handleDismiss}
      primaryAction={
        showUndo ? (
          <Banner.PrimaryAction onClick={undoHandler} disabled={isUndoing}>
            {isUndoing ? 'Undoing' : 'Undo'}
          </Banner.PrimaryAction>
        ) : undefined
      }
    />
  )
}
